import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import type { KyrgyzstanOrderData } from 'shared/api/kyrgyzstan/orders'
import { priceShow } from 'shared/lib/priceShow'

import type { CancelWarningType } from '../ui/cancel-warning-block'

interface WarningConfig {
  type: CancelWarningType
  title: string
  description: string
}

export const useCancelWarningConfig = (
  order: KyrgyzstanOrderData | null | undefined,
): WarningConfig => {
  const { t } = useTranslation()

  const isLessThan24Hours = order?.is_checkin_within_24h ?? false
  const freeCancellationHours = Math.floor(
    (order?.free_cancellation_minutes ?? 0) / 60,
  )

  const warningType: CancelWarningType = isLessThan24Hours ? 'danger' : 'info'

  return useMemo(
    () => ({
      type: warningType,
      title: isLessThan24Hours
        ? t('kyrgyzstan_block.cancel_booking.warning.danger.title', {
            time: `${freeCancellationHours} ${t('kyrgyzstan_block.time_units.hour', { count: freeCancellationHours })}`,
          })
        : t('kyrgyzstan_block.cancel_booking.warning.info.title', {
            time: `${freeCancellationHours} ${t('kyrgyzstan_block.time_units.hour', { count: freeCancellationHours })}`,
          }),
      description: isLessThan24Hours
        ? t('kyrgyzstan_block.cancel_booking.warning.danger.description', {
            time: `${freeCancellationHours} ${t('kyrgyzstan_block.time_units.hour', { count: freeCancellationHours })}`,
            amount: priceShow(String(order?.payment_amount ?? 0)),
          })
        : t('kyrgyzstan_block.cancel_booking.warning.info.description'),
    }),
    [isLessThan24Hours, t, warningType, freeCancellationHours, order],
  )
}
